import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { registerVendorEventSuccess } from '../../../../../core/vendor-events/action';
import WizardButtons from '../WizardButtons';
import { createUpdateVendorEvent } from '../../methods';
import SpaceDimensions from './SpaceDimensions';
import Amenities from './Amenities';
import { FEATURE_TYPE, SPACE_DIMENSION_TYPE, SPACE_DIMENSION_ERROR_MESSAGE } from './constants';
import { AMENITY_SPACE_STATUS } from '../../../../../constants/event-application-status';

export default function Preferences({ nextStep, previousStep }) {
  const {
    events: { event },
    vendorEvents: { vendorEventPayload, currentVendorEvent },
    venues: { venueFeatures },
  } = useSelector((store) => store);

  const dispatch = useDispatch();

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cleanedFeatures, setCleanedFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedSpaceDimension, setSelectedSpaceDimension] = useState({});
  const [totalPriceSelectedAmenities, setTotalPriceSelectedAmenities] = useState(0);

  useEffect(() => {
    if (event?.surcharges) {
      const featuresMapped = event.surcharges.map(
        (fe) =>
          fe.status === AMENITY_SPACE_STATUS.ENABLED && {
            fk_feature: fe.id,
            fk_venue: event?.venue.id,
            price: fe.price,
            name: fe.name,
            status: fe.status,
          },
      );

      const features = featuresMapped.filter((fe) => fe.status === AMENITY_SPACE_STATUS.ENABLED);
      setCleanedFeatures(features);
    }
  }, [event, event?.surcharges]);

  useEffect(() => {
    const amenitiesPrices = selectedFeatures.map((f) => Number(f.price));
    const sum = amenitiesPrices.reduce((partialSum, a) => partialSum + a, 0);
    setTotalPriceSelectedAmenities(sum);
    if (selectedFeatures.length) {
      setErrors(errors.filter((e) => e.type !== FEATURE_TYPE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatures]);

  useEffect(() => {
    if (selectedSpaceDimension?.id) {
      setErrors(errors.filter((e) => e.type !== SPACE_DIMENSION_TYPE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceDimension?.id]);

  useEffect(() => {
    if (currentVendorEvent?.vendor_event_features && cleanedFeatures.length) {
      const features = [];
      currentVendorEvent?.vendor_event_features.forEach((vef) => {
        const feature = cleanedFeatures.find(
          // eslint-disable-next-line prettier/prettier
          (cf) => cf.fk_venue === vef.fk_venue && cf.fk_feature === vef.fk_feature
        );
        if (feature) features.push(feature);
      });
      setSelectedFeatures(features);
    }
    if (currentVendorEvent?.space_dimension?.id) {
      setSelectedSpaceDimension(currentVendorEvent?.space_dimension);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendorEvent, cleanedFeatures]);

  const handleChangeFeaturesCheckboxes = (e) => {
    if (!e.target.checked) {
      setSelectedFeatures(selectedFeatures.filter((f) => f.name !== e.target.name));
      return;
    }
    const feature = cleanedFeatures.find((f) => f.name === e.target.name);
    setSelectedFeatures([...selectedFeatures, feature]);
  };

  const handleChangeSpaceDimensionCheckboxes = (e) => {
    const feature = event?.space_dimensions.find((f) => f.id === Number(e.target.id));
    setSelectedSpaceDimension(feature);
  };

  function validateErrors() {
    const newErrors = [];
    // if (selectedFeatures.length <= 0) {
    //   newErrors.push({ type: FEATURE_TYPE, message: FEATURE_ERROR_MESSAGE });
    // }
    if (!selectedSpaceDimension.id) {
      newErrors.push({ type: SPACE_DIMENSION_TYPE, message: SPACE_DIMENSION_ERROR_MESSAGE });
    }
    setErrors(newErrors);
    return newErrors;
  }

  const onNextStep = () => {
    const newErrors = validateErrors();
    if (newErrors.length) return;

    setErrors([]);
    setLoading(true);
    const type = currentVendorEvent?.state ? 'update' : 'create';
    createUpdateVendorEvent(
      {
        ...vendorEventPayload,
        amenities_price: totalPriceSelectedAmenities,
        vendor_event_features: selectedFeatures,
        space_dimension: selectedSpaceDimension.id,
      },
      // eslint-disable-next-line prettier/prettier
      type
    )
      .then((data) => {
        dispatch(registerVendorEventSuccess(data));
        nextStep();
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  return (
    <div>
      <Grid container justifyContent="center">
        <Grid item>
          <Typography fontWeight="bold">Preferences:</Typography>
        </Grid>
        <Grid item>
          <Typography marginLeft={2}>a breakdown of amenities and add-ons</Typography>
        </Grid>
      </Grid>

      <SpaceDimensions
        selectedSpaceDimension={selectedSpaceDimension}
        handleChangeCheckBoxes={handleChangeSpaceDimensionCheckboxes}
      />

      <Amenities
        cleanedFeatures={cleanedFeatures}
        handleChangeCheckBoxes={handleChangeFeaturesCheckboxes}
        selectedFeatures={selectedFeatures}
        totalPriceSelectedAmenities={totalPriceSelectedAmenities}
      />

      {errors.length > 0 &&
        errors.map((e) => (
          <Grid key={e.type} container justifyContent="center">
            <Typography color="#EE7B57">{e.message}</Typography>
          </Grid>
        ))}
      <WizardButtons nextStep={onNextStep} previousStep={previousStep} loading={loading} />
    </div>
  );
}

Preferences.propTypes = {
  nextStep: PropTypes.func,
  previousStep: PropTypes.func,
};

Preferences.defaultProps = {
  nextStep: () => {},
  previousStep: () => {},
};
